import React, { useState, useEffect, useContext } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    CircularProgress,
    TextField,
    IconButton,
    Tooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import { mapStateToProps } from "../store/helpers/mapState";
import { mapDispatchToProps } from "../store/helpers/mapDispatch";
import { connect } from "react-redux";
import SelectRecipient from "./SelectRecipient";
import moment from "moment";
import StatusLayout from "components/Status";
import BasicMenu from "./menu";
import axios from "axios";
import { errorToast, successToast } from "helpers/Notification";
import JoditEditorExample from "helpers/JoditEditorExample";
import secureLocalStorage from "react-secure-storage";
import { Socket } from "socket.io-client";
import SocketContext from "./SocketContext";

const SubmitTasks = ({
    open,
    onClose,
    onSuccess,
    SubmitGeneralForm,
    loggedUser,
    startDate: prefilledStartDate,
    endDate: prefilledEndDate,
    resources,
    linemanager,
    systemSettings,
}) => {
    const [loading, setLoading] = useState(false);
    const [reportFile, setReportFile] = useState(null);
    const [isSelectRecipientOpen, setIsSelectRecipientOpen] = useState(false);
    const [dataToEdit, setDataToEdit] = React.useState({});
    const [taskId, setTaskId] = useState(null);
    const [editChallenge, setEditChallenge] = React.useState(false);
    const formRef = React.useRef(null);
    const [validationErrors, setValidationErrors] = React.useState({});
    const socket = useContext(SocketContext);
    const [recipients, setRecipients] = useState([]);
    const mainBackground = systemSettings?.mainBackground;

    useEffect(() => {
        if (open && linemanager) {
            // console.log("Line Manager inside useEffect:", linemanager); // Log inside useEffect
            setRecipients((prev) => {
                if (prev.length > 0 && prev[0]?.id === linemanager.id) return prev;
                return [linemanager, ...prev.filter((r) => r.id !== linemanager.id)];
            });
        }
    }, [open, linemanager]);

    const handleSelectRecipients = (selectedEmployees) => {
        let updatedRecipients = selectedEmployees;
        if (linemanager) {
            updatedRecipients = [
                linemanager,
                ...selectedEmployees.filter((emp) => emp.id !== linemanager.id),
            ];
        }
        setRecipients(updatedRecipients);
        setIsSelectRecipientOpen(false);
    };

    const handleFileChange = (e) => {
        setReportFile(e.target.files[0]);
    };

    const getDefaultPeriod = () => {
        const today = moment();
        const monday = today.clone().startOf("week").add(1, "day");
        const friday = today.clone().startOf("week").add(5, "day");
        return {
            startDate: monday.format("YYYY-MM-DD HH:mm:ss"),
            endDate: friday.format("YYYY-MM-DD HH:mm:ss"),
        };
    };

    const [period, setPeriod] = useState(getDefaultPeriod());

    const [tasks, setTasks] = useState([]);

    const renderChallengeModal = () => (
        <div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                }}
            >
                <JoditEditorExample
                    htmlContent={dataToEdit?.overallStatus || ""}
                    onBlur={(str) =>
                        setDataToEdit((prev) => ({
                            ...prev,
                            overallStatus: str.target.value,
                        }))
                    }
                />
                {validationErrors.overallStatus && (
                    <div style={{ color: "red", marginTop: "0.5rem" }}>
                        {validationErrors.overallStatus}
                    </div>
                )}
            </div>
        </div>
    );
    const form = (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.preventDefault();
                updateTask()
                    .then((response) => {
                        if (response.status === 200) {
                            successToast("Task updated successfully");
                            setEditChallenge(false);
                            setDataToEdit({});
                            setValidationErrors({});
                        } else {
                            setValidationErrors(response.data);
                        }
                    })
                    .catch((error) => {
                        setValidationErrors({ description: error.message });
                    });
            }}
        >
            {renderChallengeModal()}
            <div className="px-2" style={{ display: "flex", marginTop: "1rem" }}>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => {
                                setEditChallenge(false);
                                setDataToEdit({});
                                setValidationErrors({});
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            style={{ marginLeft: "5px" }}
                            variant="contained"
                            type="submit"
                            sx={{
                                backgroundColor: mainBackground,
                                "&:hover": {
                                    backgroundColor: mainBackground,
                                },
                            }}
                        >
                            Save
                        </Button>
                    </>
                )}
            </div>
        </form>
    );

    const updateTask = async () => {
        if (!taskId) return errorToast("Server error. Please try again later");

        setLoading(true);
        try {
            const response = await axios.post(
                `/api/task/update/overall-status/${taskId}`,
                { overallStatus: dataToEdit.overallStatus }
            );
            successToast("Task updated successfully");

            setEditChallenge(false);
            setDataToEdit({});
            setValidationErrors({});
            await fetchTasks();
        } catch (error) {
            setValidationErrors({ description: error.message });
        } finally {
            setLoading(false);
            onSuccess();
        }
    };

    const columns = [
        {
            name: "name",
            label: "Name",
        },
        {
            name: "startDate",
            label: "Start Date",
            options: {
                customBodyRender: (value) => moment(value).format("DD/MM/yyyy") ?? "",
            },
        },
        {
            name: "endDate",
            label: "End Date",
            options: {
                customBodyRender: (value) => moment(value).format("DD/MM/yyyy") ?? "",
            },
        },
        {
            name: "taskInitiator",
            label: "Initiated By",
            options: {
                customBodyRender: (_, { rowData }) => {
                    const taskInitiatorId = rowData?.initiator;
                    const taskInitiatorName = rowData?.taskInitiator?.names || "Unknown";
                    const isMe = loggedUser?.Resource?.id === taskInitiatorId;
                    return isMe ? "Me" : taskInitiatorName;
                },
            },
        },
        {
            name: "id",
            label: "Comments",
            options: {
                customBodyRender: (_, { rowData }) => (
                    <div className="flex flex-row gap-4">
                        {rowData?.task_comments?.length > 0 && (
                            <Tooltip title="Comments">
                                <div className="relative flex items-center">
                                    <span className="absolute bg-red-600 text-white rounded-full text-xs px-1 top-[-6px] right-[-7px]">
                                        {rowData.task_comments.length}
                                    </span>
                                    <i className="fas fa-comment text-gray-500 text-lg" />
                                </div>
                            </Tooltip>
                        )}
                    </div>
                ),
            },
        },
        {
            name: "status",
            label: "Status",
            options: {
                customBodyRender: (status) => <StatusLayout status={status} />,
            },
        },
        {
            name: "id",
            label: "Action",
            options: {
                filter: false,
                sort: false,
                customBodyRender: (v) => (
                    <div>
                        <BasicMenu
                            extra={[
                                {
                                    label: "Edit Overall Status",
                                    icon: <i className="fas fa-pen text-sm"></i>,
                                    onClick: async () => {
                                        try {
                                            const response = await axios.get(
                                                `/api/task/get/overall-status/${v}`
                                            );
                                            const { id, name, overallStatus } = response.data;
                                            setDataToEdit({ id, name, overallStatus });
                                            // setDataToEdit(response.data.task);
                                            setTaskId(v);
                                            setEditChallenge(true);
                                        } catch (error) {
                                        } finally {
                                            setLoading(false);
                                        }
                                    },
                                },
                            ]}
                        />
                        {editChallenge && taskId === v && (
                            <Dialog
                                open={editChallenge}
                                maxWidth={"lg"}
                                fullWidth={true}
                                onClose={() => setEditChallenge(false)}
                            >
                                <DialogTitle>
                                    Edit overallStatus for Task:{" "}
                                    {dataToEdit?.name || "Unknown Task"}
                                </DialogTitle>
                                <DialogContent>{form}</DialogContent>
                            </Dialog>
                        )}
                    </div>
                ),
            },
        },
    ];

    const handleSubmit = async () => {
        if (!period || !prefilledEndDate || recipients.length === 0) {
            errorToast("Please fill in all required fields.");
            return;
        }

        setLoading(true);
        let formData = new FormData();
        formData.append(
            "body",
            JSON.stringify({
                startDate: period.startDate,
                endDate: period.endDate,
                recipients: recipients.map((r) => r.id),
            })
        );
        if (reportFile) {
            formData.append("attachment", reportFile);
        }

        try {
            const reportResponse = await SubmitGeneralForm(
                "/api/task/report/attachment",
                formData
            );
            if (reportResponse.status && reportResponse.data?.reportAttachment?.id) {
                const recipients =
                    reportResponse.data?.reportAttachment.recipients || [];
                if (recipients.length === 0) {
                    errorToast("No recipients found for the report.");
                    return;
                }
                recipients.forEach((recipient) => {
                    socket.emit("sendNotification", {
                        message: `RACI Chart created by ${loggedUser?.Resource?.names
                            } for the period ${moment(period.startDate).format(
                                "DD/MM/YYYY"
                            )} to ${moment(period.endDate).format("DD/MM/YYYY")}.`,
                        type: "raci_chart_created",
                        resourceId: loggedUser?.Resource?.id,
                        receiverId: recipient,
                    });
                });

                const reportId = reportResponse.data.reportAttachment.id;

                try {
                    const validData = tasks.filter(
                        (data) =>
                            data && data.subTask && data.taskInitiator && data.task_assignees
                    );
                    const transformedData = validData.map((data) => {
                        return {
                            reportId,
                            taskId: data.id,
                            name: data.name,
                            taskDescription: data.description,
                            status: data.status,
                            taskInitiator: data.taskInitiator.names,
                            taskAssignees: data.task_assignees.map((assignee) => ({
                                resourceId: assignee.ResourceId,
                                taskId: assignee.taskId,
                            })),
                            taskStartDate: data.startDate,
                            taskEndDate: data.endDate,
                            overallStatus: data.overallStatus,
                            subTask: data.subTask.map((subTask) => ({
                                id: subTask.id,
                                name: subTask.name,
                                description: subTask.description,
                                status: subTask.status,
                                startDate: subTask.startDate,
                                endDate: subTask.endDate,
                            })),
                        };
                    });

                    const taskResponse = await SubmitGeneralForm(
                        "/api/task/report/submit",
                        {
                            reportedTasks: transformedData,
                        }
                    );

                    if (taskResponse.status) {
                        successToast("Task Report submitted successfully.");
                        recipients.forEach((recipient) => {
                            socket.emit("sendNotification", {
                                type: "raci_chart_created",
                                resourceId: recipient,
                                receiverId: recipient,
                                message: `Task report submitted by ${loggedUser?.Resource?.names
                                    } for the period ${moment(period.startDate).format(
                                        "DD/MM/YYYY"
                                    )} to ${moment(period.endDate).format("DD/MM/YYYY")}.`,
                            });
                        });
                        onSuccess();
                        onClose();
                    } else {
                        errorToast("Failed to Submit Report.");
                    }
                } catch (error) { }
            } else {
                errorToast("Failed to Submit Report.");
            }
        } catch (error) {
            errorToast("An error occurred while submitting the report.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (open) {
            fetchTasks();
        }
    }, [open, period]);

    const fetchTasks = async () => {
        try {
            const params = {
                period: JSON.stringify(period),
                // status: "all",
            };
            const response = await axios.get("/api/task/list/assigned", { params });
            setTasks(response.data.data);
        } catch (error) { }
    };

    const handleStartDateChange = (e) => {
        const newDate = e.target.value;
        setPeriod((prev) => ({
            ...prev,
            startDate: newDate ? moment(newDate).format("YYYY-MM-DD HH:mm:ss") : null,
        }));
    };

    const handleEndDateChange = (e) => {
        const newDate = e.target.value;
        setPeriod((prev) => ({
            ...prev,
            endDate: newDate ? moment(newDate).format("YYYY-MM-DD HH:mm:ss") : null,
        }));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            sx={{
                "& .MuiDialog-paper": {
                    width: "95%",
                    maxWidth: "95%",
                    height: "95%",
                    maxHeight: "95%",
                },
            }}
        >
            <DialogTitle>
                <div className="flex items-center justify-between">
                    <span className="text-lg font-bold">
                        Submit to Generate Tasks Report
                    </span>
                    <IconButton>
                        <span className="cursor-pointer" onClick={onClose}>
                            <img
                                src={require("assets/icons/close.svg").default}
                                alt={"close"}
                            />
                        </span>
                    </IconButton>
                </div>
            </DialogTitle>
            <DialogContent dividers>
                <div className="flex gap-4 items-center mt-4">
                    <div className="flex items-center gap-2">
                        <label className="text-[#054163] font-medium">From</label>
                        <input
                            className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring focus:ring-blue-300"
                            type="date"
                            value={
                                period.startDate
                                    ? moment(period.startDate).format("YYYY-MM-DD")
                                    : ""
                            }
                            max={period.endDate}
                            onChange={handleStartDateChange}
                            required
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-[#054163] font-medium">To</label>
                        <input
                            className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring focus:ring-blue-300"
                            type="date"
                            value={
                                period.endDate
                                    ? moment(period.endDate).format("YYYY-MM-DD")
                                    : ""
                            }
                            min={moment(period.startDate).format("YYYY-MM-DD")}
                            onChange={handleEndDateChange}
                            required
                        />
                    </div>
                </div>
                <div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
                    <TextField
                        label="Recipients"
                        style={{ flex: 1 }}
                        fullWidth
                        value={recipients.map((r) => r.names).join(", ")}
                        margin="none"
                        onClick={() => setIsSelectRecipientOpen(true)}
                        InputProps={{ readOnly: true }}
                    />
                    <div
                        style={{
                            flex: 1,
                            border: "1px dashed #ccc",
                            padding: "7px",
                            textAlign: "center",
                        }}
                    >
                        <input
                            type="file"
                            accept=".pdf"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                            id="upload-file"
                        />
                        <label htmlFor="upload-file" style={{ cursor: "pointer" }}>
                            <Button component="span">Upload a file (optional)</Button>
                        </label>
                        {reportFile && <p>{reportFile.name}</p>}
                    </div>
                </div>

                <div style={{ marginTop: "24px", overflowX: "auto" }}>
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-gray-100">
                                {columns.map((column) => (
                                    <th
                                        key={column.name}
                                        className="p-3 text-left text-sm font-semibold text-gray-700 border-b"
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tasks.map((row, index) => (
                                <tr key={index} className="hover:bg-gray-50 transition-colors">
                                    {columns.map((column) => {
                                        const value = row[column.name];
                                        const renderer = column.options?.customBodyRender;

                                        return (
                                            <td
                                                key={column.name}
                                                className="p-3 text-sm text-gray-600 border-b"
                                            >
                                                {renderer
                                                    ? renderer(value, { rowData: row, rowIndex: index })
                                                    : value}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={loading || recipients.length === 0}
                    variant="contained"
                    sx={{
                        backgroundColor: mainBackground,
                        "&:hover": {
                            backgroundColor: mainBackground,
                        },
                    }}
                >
                    {loading ? <CircularProgress size={24} /> : "Submit Report"}
                </Button>
            </DialogActions>
            <SelectRecipient
                open={isSelectRecipientOpen}
                onClose={() => setIsSelectRecipientOpen(false)}
                confirm={handleSelectRecipients}
                selectedEmployees={recipients}
                resources={resources}
                settings={{ backgroundColorPrimary: "#1976d2" }}
            />
        </Dialog>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SubmitTasks);
